import {
  signInWithEmailAndPassword,
  signOut,
  updatePassword,
  onAuthStateChanged,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase/config";
import { COLLECTIONS, ERROR_MESSAGES } from "../utils/constants";
import firestoreService from "./firestoreService";

/**
 * Authentication service for admin users
 * Handles login, logout, password changes, and admin verification
 */
class AuthService {
  /**
   * Sign in an admin user with email and password
   * @param {string} email - Admin email address
   * @param {string} password - Admin password
   * @returns {Promise<Object>} User object with admin verification
   * @throws {Error} If authentication fails or user is not an admin
   */
  async signInAdmin(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const user = userCredential.user;

      // Check user_accounts collection only
      const userAccountDoc = await getDoc(
        doc(db, COLLECTIONS.USER_ACCOUNTS, email),
      );

      if (!userAccountDoc.exists()) {
        await this.signOutAdmin();
        throw new Error("Access denied. No account found for this user.");
      }

      const accountData = userAccountDoc.data();

      // Must be admin or moderator — show mobile app message for regular users
      if (accountData.isAdmin !== true && accountData.isModerator !== true) {
        await this.signOutAdmin();
        throw new Error("REGULAR_USER_WEB_LOGIN");
      }

      return {
        uid: user.uid,
        email: user.email,
        passwordChanged: accountData.passwordChanged === true,
        isAdmin: accountData.isAdmin === true,
        isModerator: accountData.isModerator === true,
      };
    } catch (error) {
      if (
        error.code === "auth/user-not-found" ||
        error.code === "auth/wrong-password" ||
        error.code === "auth/invalid-credential"
      ) {
        throw new Error(ERROR_MESSAGES.INVALID_CREDENTIALS);
      } else if (error.code === "auth/too-many-requests") {
        throw new Error(
          "Too many failed login attempts. Please try again later.",
        );
      } else if (error.code === "auth/network-request-failed") {
        throw new Error(ERROR_MESSAGES.NETWORK_ERROR);
      }
      throw new Error(error.message);
    }
  }

  /**
   * Sign out the current admin user
   * @returns {Promise<void>}
   * @throws {Error} If sign out fails
   */
  async signOutAdmin() {
    try {
      await signOut(auth);
    } catch (error) {
      if (error.code === "auth/network-request-failed") {
        throw new Error(ERROR_MESSAGES.NETWORK_ERROR);
      }
      throw new Error(error.message);
    }
  }

  /**
   * Change the current user's password
   * @param {string} currentPassword - Current password for reauthentication
   * @param {string} newPassword - New password to set
   * @returns {Promise<void>}
   * @throws {Error} If password change fails
   */
  async changePassword(currentPassword, newPassword) {
    try {
      const user = auth.currentUser;

      if (!user) {
        throw new Error("No authenticated user found.");
      }

      // Reauthenticate the user with their current password
      const credential = EmailAuthProvider.credential(
        user.email,
        currentPassword,
      );
      await reauthenticateWithCredential(user, credential);

      // Update to the new password in Firebase Auth
      await updatePassword(user, newPassword);

      // Store the new password as currentAuthPassword in Firestore so that
      // the ForgotPassword/ResetPassword flow can sign in to get an idToken.
      // Also mark passwordChanged = true.
      try {
        await firestoreService.updateUserPassword(user.email);
        await firestoreService.storeNewPassword(user.email, newPassword);
      } catch (firestoreError) {
        console.error("Error updating Firestore user account:", firestoreError);
        // Don't throw — Firebase Auth password was already changed successfully
      }
    } catch (error) {
      // Handle specific Firebase Auth errors
      if (error.code === "auth/wrong-password") {
        throw new Error("Current password is incorrect.");
      } else if (error.code === "auth/weak-password") {
        throw new Error(ERROR_MESSAGES.WEAK_PASSWORD);
      } else if (error.code === "auth/requires-recent-login") {
        throw new Error(
          "Please log out and log back in before changing your password.",
        );
      } else if (error.code === "auth/network-request-failed") {
        throw new Error(ERROR_MESSAGES.NETWORK_ERROR);
      }

      throw new Error(error.message);
    }
  }

  /**
   * Get the current authenticated user
   * @returns {Object|null} Current user object or null if not authenticated
   */
  getCurrentUser() {
    return auth.currentUser;
  }

  /**
   * Check if a user is currently authenticated
   * @returns {boolean} True if user is authenticated
   */
  isAuthenticated() {
    return !!auth.currentUser;
  }

  /**
   * Verify if the current user is an admin
   * @returns {Promise<boolean>} True if user is an admin
   */
  async isAdmin() {
    try {
      const user = auth.currentUser;
      if (!user) return false;

      const accountDoc = await getDoc(
        doc(db, COLLECTIONS.USER_ACCOUNTS, user.email),
      );
      if (accountDoc.exists()) return accountDoc.data().isAdmin === true;
      return false;
    } catch (error) {
      console.error("Error checking admin status:", error);
      return false;
    }
  }

  onAuthStateChanged(callback) {
    return onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const accountDoc = await getDoc(
            doc(db, COLLECTIONS.USER_ACCOUNTS, user.email),
          );
          if (accountDoc.exists()) {
            const data = accountDoc.data();
            callback({
              uid: user.uid,
              email: user.email,
              passwordChanged: data.passwordChanged === true,
              isAdmin: data.isAdmin === true,
              isModerator: data.isModerator === true,
            });
            return;
          }
          // No user_accounts doc
          callback({
            uid: user.uid,
            email: user.email,
            passwordChanged: true,
            isAdmin: false,
            isModerator: false,
          });
        } catch (error) {
          console.error("Error fetching user account data:", error);
          callback({
            uid: user.uid,
            email: user.email,
            passwordChanged: true,
            isAdmin: false,
            isModerator: false,
          });
        }
      } else {
        callback(null);
      }
    });
  }

  /**
   * Get authentication error message for display
   * @param {Error} error - Firebase Auth error
   * @returns {string} User-friendly error message
   */
  getErrorMessage(error) {
    switch (error.code) {
      case "auth/user-not-found":
      case "auth/wrong-password":
        return ERROR_MESSAGES.INVALID_CREDENTIALS;
      case "auth/too-many-requests":
        return "Too many failed login attempts. Please try again later.";
      case "auth/network-request-failed":
        return ERROR_MESSAGES.NETWORK_ERROR;
      case "auth/weak-password":
        return ERROR_MESSAGES.WEAK_PASSWORD;
      case "auth/requires-recent-login":
        return "Please log out and log back in to perform this action.";
      default:
        return error.message || "An unexpected error occurred.";
    }
  }
}

export default new AuthService();
